<!--
  Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->

<br/>
<h1 class="page-header">{{ powertrainIoMatrixDisplayName }}</h1>

@if (permission()) {
  <div class="icd-workflow">
    @if (saving || resetting) {
      <p-progressBar mode="indeterminate" [style]="{ height: '6px' }"></p-progressBar>
    }
    <div class="button-row">
      @if (!editExistingMode) {
        <p-button [label]="'Edit Existing ' + powertrainIoMatrixDisplayName" [disabled]="!canChangeIoMatrixContext"
                  (click)="toggleEditExistingMode(true)"></p-button>
      } @else {
        <p-button label="Use Object Code and Architecture" [disabled]="!canChangeIoMatrixContext"
                  (click)="toggleEditExistingMode(false)"></p-button>
      }
    </div>

    @if (!editExistingMode) {
      <div class="context-row">
        <label>Object Code</label>
        <p-autoComplete
          [(ngModel)]="objectCode"
          [suggestions]="filteredObjectCodeSuggestions"
          [forceSelection]="true"
          [minQueryLength]="1"
          [showClear]="true"
          placeholder="Type to search Object Code"
          (completeMethod)="filterObjectCodeSuggestions($event)"
          (onSelect)="onObjectCodeChanged()"
          (keydown.enter)="onObjectCodeInputEnter($event)"
          (onBlur)="onObjectCodeInputBlur()"
          (onClear)="onObjectCodeCleared()"></p-autoComplete>
        <label>{{ objectCodeInstLabel }}</label>
        <p-autoComplete
          [(ngModel)]="objectCodeInst"
          [suggestions]="filteredObjectCodeInstSuggestions"
          [forceSelection]="true"
          [minQueryLength]="1"
          placeholder="Type to search"
          [dropdown]="true"
          dropdownMode="blank"
          [showClear]="hasObjectCodeInstSelection"
          [disabled]="!objectCode || saving || exporting || resetting"
          (completeMethod)="filterObjectCodeInstSuggestions($event)"
          (onSelect)="onObjectCodeInstChanged($event)"
          (keydown.enter)="onObjectCodeInstInputEnter($event)"
          (onBlur)="onObjectCodeInstInputBlur()"
          (onClear)="onObjectCodeInstCleared()"></p-autoComplete>
        <label>Architecture</label>
        <p-select [options]="architectures"
                  [(ngModel)]="architecture"
                  [showClear]="!!architecture"
                  [disabled]="!objectCode || !hasObjectCodeInstSelection || saving || exporting || resetting"
                  (onChange)="onArchitectureChanged()"></p-select>
        <label>Cadence</label>
        <input pInputText [disabled]="true" [(ngModel)]="cadenceVer"/>
      </div>
    } @else {
      <div class="context-row">
        <label>Saved {{ powertrainIoMatrixDisplayName }}</label>
        <p-autoComplete
          [(ngModel)]="selectedSavedMatrixLabel"
          [suggestions]="filteredSavedIoMatrixSuggestions"
          [forceSelection]="true"
          [dropdown]="true"
          dropdownMode="blank"
          [minQueryLength]="1"
          [showClear]="true"
          [disabled]="!canChangeIoMatrixContext"
          pTooltip="Save or reset the current changes before switching IO Matrix."
          [tooltipDisabled]="canChangeIoMatrixContext"
          placeholder="Type to search Object Code | Object Code Inst | Architecture | Cadence"
          (completeMethod)="filterSavedIoMatrixSuggestions($event)"
          (onSelect)="onSavedIoMatrixSelected()"
          (onClear)="onSavedIoMatrixCleared()"></p-autoComplete>
      </div>
    }

    @if (outdatedCadence) {
      <p-message severity="warn" text="You are working with outdated Device IO information."></p-message>
    }
    @if (matrixInUse) {
      <p-message severity="warn" [closable]="true" [text]="'This ' + powertrainIoMatrixDisplayName + ' is in use by ICD templates.'"></p-message>
    }
    @if (refMapWarning) {
      <p-message severity="warn" [closable]="true" [text]="refMapWarningText"></p-message>
    }
    @if (deviceIoDriftDetected && !anyMergePreviewActive) {
      <p-message severity="warn" data-testid="device-io-drift-banner"
                 text="Live Device IO differs from this saved matrix."></p-message>
    }
    @if (deviceIoSyncPreviewActive) {
      <p-message severity="warn" data-testid="device-io-sync-summary"
                 [text]="deviceIoSyncSummary"></p-message>
    }
    @if (cadenceUpgradeAvailable) {
      <p-message severity="warn" [closable]="true" text="A newer Device IO cadence is available. Merge to carry forward Connector and Pin values."></p-message>
    }
    @if (editExistingMode && newerSavedCadenceAvailable) {
      <p-message severity="warn" [closable]="true" [text]="'A newer saved ' + powertrainIoMatrixDisplayName + ' cadence exists for this context. Use the latest cadence version unless you intentionally need historical edits.'"></p-message>
    }
    <div class="button-row">
      @if (deviceIoDriftDetected && !anyMergePreviewActive) {
        <p-button label="Sync from Device IO" icon="pi pi-sync" severity="warn" class="mr-2"
                  [disabled]="!canStartDeviceIoSync" [loading]="syncingDeviceIo"
                  (click)="startDeviceIoSync()" data-testid="device-io-sync-start"></p-button>
      }
      @if (deviceIoSyncPreviewActive) {
        <p-button label="Cancel Sync" icon="pi pi-times" severity="secondary" class="mr-2"
                  (click)="cancelDeviceIoSync()" data-testid="device-io-sync-cancel"></p-button>
      }
      @if (cadenceUpgradeAvailable) {
        <p-button label="Merge New Cadence" class="mr-2" [disabled]="saving || exporting || resetting"
                  (click)="runCadenceUpgrade()"></p-button>
      }
      <p-button label="Reset Changes" icon="pi pi-replay" severity="warn"
                class="mr-2"
                [disabled]="!dataChanged || saving || exporting || resetting"
                [loading]="saving || resetting"
                (click)="resetIoMatrixChanges()"></p-button>
      <p-button [label]="'Save ' + powertrainIoMatrixDisplayName" icon="pi pi-save"
                [disabled]="!canSaveIoMatrix() || !dataChanged || saving || exporting || resetting"
                [loading]="saving || resetting"
                (click)="saveIoMatrix()"></p-button>
    </div>

    @if (editingMatrixLabel()) {
      <div class="editing-matrix-label">
        <strong>Editing {{ powertrainIoMatrixDisplayName }}:</strong> {{ editingMatrixLabel() }}
      </div>
    }
    @if (ioMatrixDeviceNameLabel()) {
      <div class="editing-matrix-label">
        <strong>Device Name:</strong>
        @if (ioMatrixDeviceNameTooltip()) {
          <span [pTooltip]="ioMatrixDeviceNameTooltip()" tooltipPosition="top">{{ ioMatrixDeviceNameLabel() }}</span>
        } @else {
          {{ ioMatrixDeviceNameLabel() }}
        }
      </div>
    }
    <div class="context-row">
      <label>View</label>
      <p-select [options]="ioMatrixViewOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="selectedView"
                [disabled]="saving || exporting || resetting"></p-select>
      <p-button label="Add Variant"
                class="ml-2"
                [disabled]="!canSaveIoMatrix() || saving || exporting || resetting"
                (click)="openAddVariantDialog()"></p-button>
    </div>
    <ag-grid-angular style="width: 100%; height: 65vh;" class="ag-theme-balham"
                     [columnDefs]="cols"
                     [rowData]="ioMatrixRows"
                     [defaultColDef]="defaultColDef"
                     [autoSizeStrategy]="autoSizeStrategy"
                     [rowClassRules]="rowClassRules"
                     [pagination]="true"
                     [paginationPageSize]="paginationPageSize"
                     [paginationPageSizeSelector]="paginationPageSizeSelector"
                     (cellEditingStopped)="cellEditingStopped($event, ioMatrixRows)"
                     (cellValueChanged)="onIoMatrixCellValueChanged($event)"
                     (gridReady)="onGridReady($event)">
    </ag-grid-angular>

    <p-dialog header="Add Variant"
              [(visible)]="addVariantDialogVisible"
              [modal]="true"
              [closable]="true"
              [draggable]="false"
              [resizable]="false"
              [style]="{width: '28rem'}"
              [breakpoints]="{'640px': '96vw'}"
              (onHide)="closeAddVariantDialog()">
      <div class="add-variant-dialog">
        <div class="add-variant-row">
          <label class="add-variant-label" for="addVariantUsage">Usage</label>
          <input id="addVariantUsage"
                 class="add-variant-input"
                 pInputText
                 [(ngModel)]="newVariantUsage"
                 [disabled]="saving || resetting"
                 placeholder="e.g. P02A"/>
        </div>
        <div class="add-variant-row">
          <label class="add-variant-label" for="addVariantPartNumber">Part Number</label>
          <input id="addVariantPartNumber"
                 class="add-variant-input"
                 pInputText
                 [(ngModel)]="newVariantPartNumber"
                 [disabled]="saving || resetting"
                 placeholder="e.g. 12345678"/>
        </div>
        <p class="add-variant-hint">Creates a variant token as Usage-PartNumber (for example P02A-12345678).</p>
      </div>
      <ng-template pTemplate="footer">
        <p-button label="Cancel" severity="secondary" (click)="closeAddVariantDialog()" class="mr-2"></p-button>
        <p-button label="Add"
                  [disabled]="saving || resetting || !addVariantDialogValid"
                  (click)="executeAddVariant()"></p-button>
      </ng-template>
    </p-dialog>
  </div>
}
